import React from 'react';
import PropTypes from 'prop-types';
import ModalLayout from '../ModalLayout';
import BasicModalLayoutFooter from '../ModalLayout/BasicModalLayoutFooter';
import _ from 'lodash';
import {
  getValidationsObject,
  setFocus,
  isValidCustomer,
  getFirstFieldWithError,
} from '../../utils/customerInformationUtils';
import Fader from '../Fader';
import FaderTitle from '../Fader/FaderTitle';
import { getLatestComment } from '../../utils/OrderUtils';
import { translate } from 'react-i18next';
import Comment from '../Comment';
import { PhoneUtils } from '@wix/wixrest-utils';
import OnBoardingStepper from '../OnBoardingStepper';
import { Input, Dropdown, Modal, TextButton, Layout, Cell, FormField, Text } from 'wix-style-react';

const NEW_CONTACT_ID = '_new-contact_';

// phoneordering_customerinfo_contactdetails_title_returning
// phoneordering_customerinfo_contactdetails_returning_name_addnew_cta
// phoneordering_customerinfo_contactdetails_returning_name_addnew_popup_done
// phoneordering_customerinfo_contactdetails_returning_next_cta

class CustomerInformation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      indicateErrors: {},
      localCustomer: props.customer,
      localComment: getLatestComment(props.comments),
      isExistingCustomer: props.contacts.length > 0,
      displayTitleBackButton: false,
      disabled: false,
    };
    this.dropdownRef = React.createRef();
  }

  inputs = { firstNameRef: React.createRef(), lastNameRef: React.createRef(), emailRef: React.createRef() };

  static propTypes = {
    contacts: PropTypes.array,
    updateCustomer: PropTypes.func,
    isModal: PropTypes.bool,
    onRequestClose: PropTypes.func,
    comments: PropTypes.array,
    addComment: PropTypes.func,
    user: PropTypes.object,
    customerDetailsDone: PropTypes.func,
    goBack: PropTypes.func,
    t: PropTypes.func,
    countryCode: PropTypes.string,
    locale: PropTypes.string,
    reportCustomerInformationContinueAttempt: PropTypes.func,
  };

  static getDerivedStateFromProps(props, state) {
    const { localCustomer } = state;
    const { firstName, lastName } = localCustomer;
    const disabled = Boolean(!firstName || !lastName);

    return { disabled };
  }

  onInputChange = (field, value) => {
    const { localCustomer } = this.state;

    this.setState({
      indicateErrors: { ...this.state.indicateErrors, [field]: false },
      localCustomer: { ...localCustomer, [field]: value },
    });
  };

  getInputProps = (field) => {
    const { indicateErrors, localCustomer } = this.state;
    const { countryCode, t } = this.props;
    const validations = getValidationsObject(localCustomer);
    const value = localCustomer[field];
    const hasError = indicateErrors[field] && validations[field];
    return {
      value: (field !== 'phone' ? value : PhoneUtils.displayPhone(this.state.localCustomer.phone, countryCode)) || '',
      onChange: (e) => this.onInputChange(field, e.target.value),
      status: hasError ? Input.StatusError : null,
      dataHook: `customer-info-${_.kebabCase(field)}`,
      ref: this.inputs[`${field}Ref`],
      placeholder: '',
      statusMessage: hasError && field === 'email' ? t('phoneordering_customerinfo_contactdetails_email_error') : null,
    };
  };

  indicateErrorsOnAllFields = () => {
    this.setState({ indicateErrors: { firstName: true, lastName: true, email: true } });
  };

  onFormSubmit = (event) => {
    const {
      updateCustomer,
      addComment,
      customerDetailsDone,
      isModal,
      reportCustomerInformationContinueAttempt,
    } = this.props;
    const { localCustomer, localComment, isExistingCustomer } = this.state;
    event && event.preventDefault();

    if (!this.state.disabled) {
      this.indicateErrorsOnAllFields();
      setFocus({ ...this.inputs, customer: localCustomer });

      if (isValidCustomer(localCustomer)) {
        addComment({ text: localComment.text, userId: 'HARD CODED' });
        updateCustomer({ customer: localCustomer });
        customerDetailsDone();

        if (isModal) {
          this.onRequestClose();
        }
      }

      reportCustomerInformationContinueAttempt({
        errorField: getFirstFieldWithError({ customer: localCustomer }),
        isError: !isValidCustomer(localCustomer),
        isNewCustomer: !isExistingCustomer,
      });
    }
  };

  onSelect = ({ id }) => {
    if (id === NEW_CONTACT_ID) {
      this.setState({
        displayTitleBackButton: true,
        isExistingCustomer: false,
        localCustomer: { phone: this.state.localCustomer.phone },
        indicateErrors: {},
      });
    } else {
      const customer = this.props.contacts.find((c) => c.id === id);
      this.setState({ localCustomer: customer });
    }
  };

  onTitleBackClick = () => {
    this.setState({
      displayTitleBackButton: false,
      isExistingCustomer: true,
      localCustomer: this.props.contacts[0],
    });
  };

  onCommentTextChange = (text) => {
    this.setState({
      localComment: {
        text,
      },
    });
  };

  onRequestClose = () => {
    if (this.props.onRequestClose) {
      this.props.onRequestClose();
    } else {
      this.props.goBack();
    }
  };

  wrapIfNeeded = (renderedContent) => {
    const { isModal, isOpen } = this.props;

    if (isModal) {
      return (
        <Modal isOpen={isOpen} onRequestClose={this.onRequestClose} shouldCloseOnOverlayClick>
          {renderedContent}
        </Modal>
      );
    } else {
      return renderedContent;
    }
  };

  onDropdownEnterPressed = () => {
    if (!this.dropdownRef.current.isDropdownLayoutVisible()) {
      this.onFormSubmit();
    }
  };

  render() {
    const { isModal, t } = this.props;
    const { isExistingCustomer, displayTitleBackButton, disabled } = this.state;

    const title = (
      <FaderTitle
        primary={t('phoneordering_customerinfo_contactdetails_title_returning')}
        secondary={t('phoneordering_customerinfo_contactdetails_title_new')}
        drilldown={!isExistingCustomer}
        displayBackButton={displayTitleBackButton}
        onBackClick={this.onTitleBackClick}
      />
    );

    const okText = isModal
      ? t('phoneordering_customerinfo_ordertype_address_addnew_popup_cta1')
      : t('phoneordering_customerinfo_contactdetails_new_next_cta');
    const secondaryText = isModal
      ? t('phoneordering_customerinfo_ordertype_address_addnew_popup_cta2')
      : t('phoneordering_customerinfo_contactdetails_back_cta');

    const footer = (
      <BasicModalLayoutFooter
        form
        okAutoFocus
        okText={okText}
        disabled={disabled}
        cancelText="Cancel"
        onCancelClick={this.onRequestClose}
        displayCancelButton={Boolean(isModal)}
        displaySecondaryButton={!isModal}
        secondaryText={secondaryText}
        onSecondaryClick={this.onRequestClose}
      />
    );

    const primary = this.renderForm({ isExistingCustomer: true });
    const secondary = this.renderForm({ isExistingCustomer: false });
    const prefix = <OnBoardingStepper />;

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && e.target.getAttribute('data-hook') !== 'footer-secondary-button') {
        e.preventDefault();

        if (!disabled) {
          this.onFormSubmit();
        }
      }
    };

    const renderedContent = (
      <form onSubmit={this.onFormSubmit} onKeyDown={handleKeyDown} data-hook="customer-information-wrapper">
        <ModalLayout
          card={!isModal}
          title={title}
          footer={footer}
          scrollable={isModal}
          onCloseClick={this.onRequestClose}
          prefix={!isModal && prefix}
        >
          <div data-hook="customer-info-wrapper">
            <Fader primary={primary} secondary={secondary} drilldown={!isExistingCustomer} delayedEnter dynamicHeight />
          </div>
        </ModalLayout>
      </form>
    );

    return this.wrapIfNeeded(renderedContent);
  }

  renderForm({ isExistingCustomer }) {
    const { contacts, t, locale } = this.props;
    const { localCustomer, localComment } = this.state;

    const options = [
      ...contacts.map((contact) => ({ id: contact.id, value: `${contact.firstName} ${contact.lastName}` })),
      {
        id: NEW_CONTACT_ID,
        value: <TextButton>{t('phoneordering_customerinfo_contactdetails_returning_name_addnew_cta')}</TextButton>,
      },
    ];

    return (
      <Layout>
        {!isExistingCustomer && (
          <Cell span={6}>
            <FormField
              required
              label={
                <Text size="medium" secondary>
                  {t('phoneordering_customerinfo_contactdetails_firstname_label')}
                </Text>
              }
            >
              <Input {...this.getInputProps('firstName')} autoFocus />
            </FormField>
          </Cell>
        )}
        {!isExistingCustomer && (
          <Cell span={6}>
            <FormField
              required
              label={
                <Text size="medium" secondary>
                  {t('phoneordering_customerinfo_contactdetails_lastname_label')}
                </Text>
              }
            >
              <Input {...this.getInputProps('lastName')} />
            </FormField>
          </Cell>
        )}
        {isExistingCustomer && (
          <Cell span={12}>
            <FormField
              required
              label={
                <Text size="medium" secondary>
                  {t('phoneordering_customerinfo_contactdetails_returning_name_label')}
                </Text>
              }
            >
              <Dropdown
                options={options}
                selectedId={localCustomer.id}
                onSelect={this.onSelect}
                dataHook="customer-info-full-name"
                autoFocus
                onEnterPressed={this.onDropdownEnterPressed}
                ref={this.dropdownRef}
              />
            </FormField>
          </Cell>
        )}
        <Cell span={6}>
          <FormField
            label={
              <Text size="medium" secondary>
                {t('phoneordering_customerinfo_contactdetails_phonenumber_label')}
              </Text>
            }
          >
            <Input {...this.getInputProps('phone')} readOnly />
          </FormField>
        </Cell>
        <Cell span={6}>
          <FormField
            label={
              <Text size="medium" secondary>
                {t('phoneordering_customerinfo_contactdetails_email_label')}
              </Text>
            }
          >
            <Input {...this.getInputProps('email')} />
          </FormField>
        </Cell>
        {isExistingCustomer && (
          <Cell span={12}>
            <Comment comment={localComment} onCommentTextChange={this.onCommentTextChange} locale={locale} />
          </Cell>
        )}
      </Layout>
    );
  }
}

CustomerInformation.displayName = 'CustomerInformation';

export default translate()(CustomerInformation);
